import fetcher from '../../utils/fetcher';
import intersectionObserver from '../../utils/intersectionObserver';
import getInjector from '../../utils/getInjector';
import getAngularScope from '../../utils/getAngularScope';
import { PAGE_TYPE } from '../../constants/logger';
import trackerService from '../../service/trackerService';
import { EVENT_NAME_FOR_PRODUCT_LIST } from '../../../packs/shop/constants/tracker';
import { stockStatus } from './constants';

export class LazyLoadRelatedProducts extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.productObserver = null;
    this.nodeDataMap = new Map();
    this.productId = null;
    this.targetNodeId = 'relatedProductList';
  }

  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.productId = this.getAttribute('data-product-id');
    if (this.productId) {
      this.observer = intersectionObserver(
        (entry, observer) => {
          this.initRelatedProducts();
          observer.unobserve(this);
        },
        {
          root: null,
          rootMargin: '100px',
          threshold: 0,
        },
      );
      this.observer?.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.productObserver?.disconnect();
  }

  initRelatedProducts() {
    return fetchRecommendProductListApi(this.productId).then(
      (relatedProducts) => {
        if (relatedProducts?.components && relatedProducts?.products) {
          this.render({
            components: relatedProducts.components,
            products: relatedProducts.products,
          });
        }
      },
    );
  }

  removeLoadingElementAndHide() {
    this.style.display = 'none';
    this.replaceChildren();
  }

  getProductNodes() {
    return document.querySelectorAll(
      `#${this.targetNodeId} > :not(lazyload-related-products)`,
    );
  }

  setupNodeDataMap(products) {
    const relatedProductNodes = this.getProductNodes();

    relatedProductNodes?.forEach((node, index) => {
      // use the element as key to set the related product data
      this.nodeDataMap.set(node, {
        relatedProduct: products[index],
        order: index + 1,
      });
    });
  }

  initProductIntersectionObserver() {
    const relatedProductNodes = this.getProductNodes();
    this.productObserver = intersectionObserver((entry, observer) => {
      this.trackProductVisible(entry.target, observer);
      observer.unobserve(entry.target);
    });
    relatedProductNodes.forEach((node) => this.productObserver.observe(node));
  }

  initProductClickEvent() {
    const relatedProductNodes = this.getProductNodes();
    relatedProductNodes.forEach((element) => {
      element.addEventListener('click', () => {
        const { relatedProduct, order } = this.nodeDataMap.get(element) || {};
        if (relatedProduct && order) {
          sendRecommendItem({
            productId: this.productId,
            relatedProductId: relatedProduct.id,
            order,
            recStrategy: relatedProduct.rec_strategy,
          });
        }
      });
    });
  }

  trackProductVisible(element) {
    const { relatedProduct, order } = this.nodeDataMap.get(element) || {};
    if (relatedProduct && order) {
      sendViewRelatedProducts({
        relatedProduct,
        productId: this.productId,
        order,
      });
    }
  }

  initEvent(products) {
    const targetNode = document.getElementById(this.targetNodeId);
    initAngularDigest(targetNode);
    this.setupNodeDataMap(products);
    this.initProductIntersectionObserver();
    this.initProductClickEvent();
  }

  render({ components, products }) {
    const targetNode = document.getElementById(this.targetNodeId);
    if (targetNode) {
      const documentFragment = document
        .createRange()
        .createContextualFragment(components.join(''));
      targetNode.appendChild(documentFragment);
      this.removeLoadingElementAndHide();
      this.initEvent(products);
    }
  }
}

if (!customElements.get('lazyload-related-products')) {
  customElements.define('lazyload-related-products', LazyLoadRelatedProducts);
}

export const sendRecommendItem = ({
  productId,
  relatedProductId,
  order,
  recStrategy,
}) => {
  const params = {
    product_id: productId,
    based_product_id: relatedProductId,
    rank: order,
    ...(recStrategy && { rec_strategy: recStrategy }),
  };
  trackerService.track(EVENT_NAME_FOR_PRODUCT_LIST.RecommendItem, params);
};

export const sendViewRelatedProducts = ({
  relatedProduct,
  productId,
  order,
}) => {
  trackerService.track(EVENT_NAME_FOR_PRODUCT_LIST.ViewRelatedProducts, {
    product_infos: [
      {
        id: relatedProduct.id,
        status: relatedProduct.sold_out
          ? stockStatus.OUT_OF_STOCK
          : stockStatus.IN_STOCK,
        rank: order,
        based_product_id: productId,
        ...(relatedProduct.rec_strategy && {
          rec_strategy: relatedProduct.rec_strategy,
        }),
      },
    ],
  });
};

export const initAngularDigest = async (element) => {
  const injector = await getInjector(PAGE_TYPE.pdp);
  // product-item wishlist need to get scope from MainController
  const $MainControllerScope = await getAngularScope(
    PAGE_TYPE.pdp,
    document.querySelector('[ng-controller="MainController"]'),
  );
  if (injector && $MainControllerScope) {
    const $compile = injector.get('$compile');
    $compile(element)($MainControllerScope);
    $MainControllerScope.$digest();
  }
};

/**
 * Fetch recommended products for a given product id.
 * @param {string} productId - product id
 * @returns {Promise<{components: [string, string], products: [{ id: string, sold_out: boolean,rec_strategy: string }]} | null>} A promise that resolves to the API response containing components or null if the request fails.
 */
export const fetchRecommendProductListApi = async (productId) =>
  fetcher(`/api/renders/related_products?product_id=${productId}`, {
    method: 'GET',
  })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
      return null;
    })
    .catch((error) => {
      console.error('Failed to fetch related products:', error);
      return null;
    });
